define((require) => {
    "use strict";

    const _ = require("underscore");

    /**
     * This class is used to formalize a Mix Panel event naming convention.
     * When adding new events, the below structure must be followed, either re-using or defining new actions and targets.
     *
     * When modifying the below, update relative documentation on Confluence: https://confluence.zmags.com/display/MOS/Mix+Panel+Events.
     */

    const SEPARATOR = ":";

    /**
     * Object describing the action for the tracking event.
     */
    const Actions = {
        ACCESS:    "access",
        ADD:       "add",
        ATTACH:    "attach",
        COPY:      "copy",
        CLOSE:     "close",
        CREATE:    "create",
        DELETE:    "delete",
        DETACH:    "detach",
        DUPLICATE: "duplicate",
        EDIT:      "edit",
        ERROR:     "error",
        EXPORT:    "export",
        GET:       "get",
        GROUP:     "group",
        INVITE:    "invite",
        LIST:      "list",
        MOVE:      "move",
        OPEN:      "open",
        PASTE:     "paste",
        PUBLISH:   "publish",
        PUSH:      "push",
        REDO:      "redo",
        REMOVE:    "remove",
        SAVE:      "save",
        SELECT:    "select",
        SHARE:     "share",
        SORT:      "sort",
        SWITCH:    "switch",
        TEST:      "test",
        TOGGLE:    "toggle",
        UNDO:      "undo",
        UNGROUP:   "ungroup",
        UPDATE:    "update",
        UPLOAD:    "upload",
        ZOOM:      "zoom"
    };

    /**
     * Object describing the targets for the tracking event. Can be models or ui components.
     */
    const Targets = {
        Model: {
            ACTIVITY:       "activity",
            BACKDROP:       "backdrop",
            BREAKPOINT:     "breakpoint",
            NATIVE_CODE:     "native-code",
            CHANNEL:        "channel",
            COMMENT:        "comment",
            COMMENT_THREAD: "comment-thread",
            EXPORT:         "export",
            FONT:           "font",
            EXPERIENCE:     "experience",
            GROUP:          "group",
            GUIDELINE:      "guideline",
            IMAGE:          "image",
            IMAGE_TAG:      "image-tag",
            INTEGRATION:    "integration",
            PROJECT:        "project",
            SCENE:          "scene",
            SNAPPING:       "snapping",
            SNIPPET:        "snippet",
            TEMPLATE:       "template",
            USER:           "user",
            VARIANT:        "variant",
            WIDGET:         "widget"
        },
        Ui: {
            COLOR:                    "color",
            DASHBOARD:                "dashboard",
            DIRECT_URL:               "direct-url",
            DRAG_AND_DROP_PUBLISHING: "drag-and-drop-publish",
            EXPERIENCE_EDITOR:        "experience-editor",
            EXPERIENCE_PREVIEW:       "experience-preview",
            HELP_LINK:                "help-link",
            PROJECT_SELECTOR:         "project-selector",
            MASTER_LAYERS:            "master-layers",
            MODAL_WINDOW:             "modal-window",
            REGISTRATION_PAGE:        "registration-page",
            RULERS_AND_GUIDELINES:    "rulers-and-guidelines",
            SHARE_MENU:               "share-menu",
            SHARE_EMAIL_RECIPIENT:    "share-email-recipient",
            SOURCE_SELECTOR:          "image-source-selector",
            TEMPLATE_PREVIEW:         "template-preview",
            TOUR_GUIDE:               "tour-guide"
        }
    };

    const EventNames = {
        ACCESS_USER:                  _buildEventName(Actions.ACCESS, Targets.Model.USER),
        ADD_BREAKPOINT:               _buildEventName(Actions.ADD, Targets.Model.BREAKPOINT),
        ADD_CHANNEL:                  _buildEventName(Actions.ADD, Targets.Model.CHANNEL),
        ADD_GUIDELINE:                _buildEventName(Actions.ADD, Targets.Model.GUIDELINE),
        ADD_IMAGE_TAG:                _buildEventName(Actions.ADD, Targets.Model.IMAGE_TAG),
        ADD_SHARE_EMAIL_RECIPIENT:    _buildEventName(Actions.ADD, Targets.Ui.SHARE_EMAIL_RECIPIENT),
        ADD_WIDGET:                   _buildEventName(Actions.ADD, Targets.Model.WIDGET),
        ATTACH_CHANNEL:               _buildEventName(Actions.ATTACH, Targets.Model.CHANNEL),
        ATTACH_EXPERIENCE:            _buildEventName(Actions.ATTACH, Targets.Model.EXPERIENCE),
        ATTACH_GROUP:                 _buildEventName(Actions.ATTACH, Targets.Model.GROUP),
        ATTACH_IMAGE:                 _buildEventName(Actions.ATTACH, Targets.Model.IMAGE),
        CLOSE_MODAL_WINDOW:           _buildEventName(Actions.CLOSE, Targets.Ui.MODAL_WINDOW),
        COPY_CODE:                    _buildEventName(Actions.COPY, Targets.Model.NATIVE_CODE),
        COPY_SNIPPET:                 _buildEventName(Actions.COPY, Targets.Model.SNIPPET),
        COPY_TEMPLATE:                _buildEventName(Actions.COPY, Targets.Model.TEMPLATE),
        COPY_WIDGET:                  _buildEventName(Actions.COPY, Targets.Model.WIDGET),
        CREATE_COMMENT:               _buildEventName(Actions.CREATE, Targets.Model.COMMENT),
        CREATE_COMMENT_THREAD:        _buildEventName(Actions.CREATE, Targets.Model.COMMENT_THREAD),
        CREATE_EXPERIENCE:            _buildEventName(Actions.CREATE, Targets.Model.EXPERIENCE),
        CREATE_GROUP:                 _buildEventName(Actions.CREATE, Targets.Model.GROUP),
        CREATE_PROJECT:               _buildEventName(Actions.CREATE, Targets.Model.PROJECT),
        CREATE_SCENE:                 _buildEventName(Actions.CREATE, Targets.Model.SCENE),
        CREATE_USER:                  _buildEventName(Actions.CREATE, Targets.Model.USER),
        CREATE_VARIANT:               _buildEventName(Actions.CREATE, Targets.Model.VARIANT),
        DELETE_BREAKPOINT:            _buildEventName(Actions.DELETE, Targets.Model.BREAKPOINT),
        DELETE_CHANNEL:               _buildEventName(Actions.DELETE, Targets.Model.CHANNEL),
        DELETE_COMMENT_THREAD:        _buildEventName(Actions.DELETE, Targets.Model.COMMENT_THREAD),
        DELETE_EXPERIENCE:            _buildEventName(Actions.DELETE, Targets.Model.EXPERIENCE),
        DELETE_FONT:                  _buildEventName(Actions.DELETE, Targets.Model.FONT),
        DELETE_GUIDELINE:             _buildEventName(Actions.DELETE, Targets.Model.GUIDELINE),
        DELETE_INTEGRATION:           _buildEventName(Actions.DELETE, Targets.Model.INTEGRATION),
        DELETE_PROJECT:               _buildEventName(Actions.DELETE, Targets.Model.PROJECT),
        DELETE_SCENE:                 _buildEventName(Actions.DELETE, Targets.Model.SCENE),
        DELETE_VARIANT:               _buildEventName(Actions.DELETE, Targets.Model.VARIANT),
        DELETE_WIDGET:                _buildEventName(Actions.DELETE, Targets.Model.WIDGET),
        DELETE_USER:                  _buildEventName(Actions.DELETE, Targets.Model.USER),
        DETACH_CHANNEL:               _buildEventName(Actions.DETACH, Targets.Model.CHANNEL),
        DETACH_EXPERIENCE:            _buildEventName(Actions.DETACH, Targets.Model.EXPERIENCE),
        DETACH_GROUP:                 _buildEventName(Actions.DETACH, Targets.Model.GROUP),
        DETACH_IMAGE:                 _buildEventName(Actions.DETACH, Targets.Model.IMAGE),
        DUPLICATE_EXPERIENCE:         _buildEventName(Actions.DUPLICATE, Targets.Model.EXPERIENCE),
        DUPLICATE_GROUP:              _buildEventName(Actions.DUPLICATE, Targets.Model.GROUP),
        DUPLICATE_SCENE:              _buildEventName(Actions.DUPLICATE, Targets.Model.SCENE),
        EDIT_IMAGE_TAG:               _buildEventName(Actions.EDIT, Targets.Model.IMAGE_TAG),
        ERROR:                        Actions.ERROR,
        EXPORT_EXPORT:                _buildEventName(Actions.EXPORT, Targets.Model.EXPORT),
        GET_CODE:                     _buildEventName(Actions.GET, Targets.Model.NATIVE_CODE),
        GROUP_WIDGETS:                _buildEventName(Actions.GROUP, Targets.Model.WIDGET),
        INVITE_USER:                  _buildEventName(Actions.INVITE, Targets.Model.USER),
        LIST_TEMPLATES:               _buildEventName(Actions.LIST, Targets.Model.TEMPLATE),
        MOVE_SCENE:                   _buildEventName(Actions.MOVE, Targets.Model.SCENE),
        OPEN_DASHBOARD:               _buildEventName(Actions.OPEN, Targets.Ui.DASHBOARD),
        OPEN_DIRECT_URL:              _buildEventName(Actions.OPEN, Targets.Ui.DIRECT_URL),
        OPEN_DRAG_AND_DROP_PUBLISH:   _buildEventName(Actions.OPEN, Targets.Ui.DRAG_AND_DROP_PUBLISHING),
        OPEN_EXPERIENCE_EDITOR:       _buildEventName(Actions.OPEN, Targets.Ui.EXPERIENCE_EDITOR),
        OPEN_EXPERIENCE_PREVIEW:      _buildEventName(Actions.OPEN, Targets.Ui.EXPERIENCE_PREVIEW),
        OPEN_TEMPLATE_PREVIEW:        _buildEventName(Actions.OPEN, Targets.Ui.TEMPLATE_PREVIEW),
        OPEN_HELP_LINK:               _buildEventName(Actions.OPEN, Targets.Ui.HELP_LINK),
        OPEN_MASTER_LAYERS:           _buildEventName(Actions.OPEN, Targets.Ui.MASTER_LAYERS),
        OPEN_MODAL_WINDOW:            _buildEventName(Actions.OPEN, Targets.Ui.MODAL_WINDOW),
        OPEN_PROJECT_SELECTOR:        _buildEventName(Actions.OPEN, Targets.Ui.PROJECT_SELECTOR),
        OPEN_REGISTRATION_PAGE:       _buildEventName(Actions.OPEN, Targets.Ui.REGISTRATION_PAGE),
        OPEN_SHARE_MENU:              _buildEventName(Actions.OPEN, Targets.Ui.SHARE_MENU),
        PASTE_WIDGET:                 _buildEventName(Actions.PASTE, Targets.Model.WIDGET),
        PUBLISH_CHANNEL:              _buildEventName(Actions.PUBLISH, Targets.Model.CHANNEL),
        PUSH_EXPERIENCE:              _buildEventName(Actions.PUSH, Targets.Model.EXPERIENCE),
        PUSH_GROUP:                   _buildEventName(Actions.PUSH, Targets.Model.GROUP),
        REDO_ACTIVITY:                _buildEventName(Actions.REDO, Targets.Model.ACTIVITY),
        REMOVE_IMAGE_TAG:             _buildEventName(Actions.REMOVE, Targets.Model.IMAGE_TAG),
        SAVE_BACKDROP:                _buildEventName(Actions.SAVE, Targets.Model.BACKDROP),
        SAVE_BREAKPOINT:              _buildEventName(Actions.SAVE, Targets.Model.BREAKPOINT),
        SAVE_INTEGRATION:             _buildEventName(Actions.SAVE, Targets.Model.INTEGRATION),
        SAVE_TEMPLATE:                _buildEventName(Actions.SAVE, Targets.Model.TEMPLATE),
        SELECT_COLOR:                 _buildEventName(Actions.SELECT, Targets.Ui.COLOR),
        SELECT_IMAGE:                 _buildEventName(Actions.SELECT, Targets.Model.IMAGE),
        SELECT_SOURCE:                _buildEventName(Actions.SELECT, Targets.Ui.SOURCE_SELECTOR),
        SHARE_EXPERIENCE:             _buildEventName(Actions.SHARE, Targets.Model.EXPERIENCE),
        SORT_DASHBOARD:               _buildEventName(Actions.SORT, Targets.Ui.DASHBOARD),
        SORT_TEMPLATE:                _buildEventName(Actions.SORT, Targets.Model.TEMPLATE),
        SWITCH_VARIANT:               _buildEventName(Actions.SWITCH, Targets.Model.VARIANT),
        TEST:                         Actions.TEST, // Used for frontend unit testing.
        TOGGLE_RULERS_AND_GUIDELINES: _buildEventName(Actions.TOGGLE, Targets.Ui.RULERS_AND_GUIDELINES),
        TOGGLE_SNAPPING:              _buildEventName(Actions.TOGGLE, Targets.Model.SNAPPING),
        UNDO_ACTIVITY:                _buildEventName(Actions.UNDO, Targets.Model.ACTIVITY),
        UNGROUP_WIDGETS:              _buildEventName(Actions.UNGROUP, Targets.Model.WIDGET),
        UPDATE_BACKDROP:              _buildEventName(Actions.UPDATE, Targets.Model.BACKDROP),
        UPDATE_BREAKPOINT:            _buildEventName(Actions.UPDATE, Targets.Model.BREAKPOINT),
        UPDATE_CHANNEL:               _buildEventName(Actions.UPDATE, Targets.Model.CHANNEL),
        UPDATE_EXPERIENCE:            _buildEventName(Actions.UPDATE, Targets.Model.EXPERIENCE),
        UPDATE_GROUP:                 _buildEventName(Actions.UPDATE, Targets.Model.GROUP),
        UPDATE_IMAGE:                 _buildEventName(Actions.UPDATE, Targets.Model.IMAGE),
        UPDATE_PROJECT:               _buildEventName(Actions.UPDATE, Targets.Model.PROJECT),
        UPDATE_USER:                  _buildEventName(Actions.UPDATE, Targets.Model.USER),
        UPDATE_VARIANT:               _buildEventName(Actions.UPDATE, Targets.Model.VARIANT),
        UPDATE_WIDGET:                _buildEventName(Actions.UPDATE, Targets.Model.WIDGET),
        UPLOAD_FONT:                  _buildEventName(Actions.UPLOAD, Targets.Model.FONT),
        UPLOAD_IMAGE:                 _buildEventName(Actions.UPLOAD, Targets.Model.IMAGE),
        ZOOM_SCENE:                   _buildEventName(Actions.ZOOM, Targets.Model.SCENE),
        ZOOM_PREVIEW:                 _buildEventName(Actions.ZOOM, Targets.Ui.EXPERIENCE_PREVIEW)
    };

    /**
     * Builds the event name to be sent to Mix Panel via Segment.
     * @param {String} action An action name as defined in the Actions object.
     * @param {String} target A target name as defined in the Targets object;
     * @returns {String} The colon-separated event name.
     * @private
     */
    function _buildEventName(action, target) {
        return action + SEPARATOR + target;
    }

    /**
     * Utility method to verify that the supplied event name exists in the EventNames list.
     * @param {String} eventName The name of the event.
     * @return {Boolean} True if event name exists in the EventNames object, false otherwise.
     */
    function checkEvent(eventName) {
        const values = _.values(EventNames);
        return _.contains(values, eventName);
    }

    return {
        EventNames,
        checkEvent
    };
});



// WEBPACK FOOTER //
// ./src/shared/services/EditorEventsConstants.js